import { useState } from 'react'
import {
  Card,
  CardContent
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  DriverUpdateRow,
  FieldConflictResolution,
  UnmatchedDriverAction
} from '@/types'
import type { DriverMaster } from '@/types/driverMaster'

interface DriverMatchReviewStepProps {
  driverUpdateRows: DriverUpdateRow[]
  availableDrivers: DriverMaster[]
  conflictResolutions: Map<string, FieldConflictResolution[]>
  unmatchedActions: Map<string, UnmatchedDriverAction>
  onConflictResolutionChange: (rowId: string, conflict: FieldConflictResolution) => void
  onUnmatchedActionChange: (rowId: string, action: UnmatchedDriverAction) => void
  onMatchRejected: (rowId: string) => void
  onMatchRestored: (rowId: string) => void
}

export function DriverMatchReviewStep({
  driverUpdateRows,
  availableDrivers,
  conflictResolutions,
  unmatchedActions,
  onConflictResolutionChange,
  onUnmatchedActionChange,
  onMatchRejected,
  onMatchRestored
}: DriverMatchReviewStepProps) {
  // Calculate summary stats
  // A row with hasMatch=true but present in unmatchedActions is a rejected match — treat as unmatched
  const isRejected = (r: DriverUpdateRow) => r.hasMatch && unmatchedActions.has(r.rowId)
  const matched = driverUpdateRows.filter(r => r.hasMatch && !isRejected(r) && (!r.conflicts || r.conflicts.length === 0)).length
  const conflicts = driverUpdateRows.filter(r => r.hasMatch && !isRejected(r) && r.conflicts && r.conflicts.length > 0).length
  const unmatched = driverUpdateRows.filter(r => !r.hasMatch || isRejected(r)).length

  return (
    <div className="space-y-3">
      {/* Summary stats */}
      <Card className="bg-muted/50">
        <CardContent className="py-3">
          <div className="flex gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Matchade:</span>
              <Badge variant="outline" className="text-green-600 border-green-500">{matched}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Omatchade:</span>
              <Badge variant="outline" className="text-yellow-600 border-yellow-500">{unmatched}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Konflikter:</span>
              <Badge variant="outline" className="text-orange-600 border-orange-500">{conflicts}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compact driver rows */}
      <div className="space-y-1">
        {driverUpdateRows.map((row) => (
          <CompactDriverRow
            key={row.rowId}
            row={row}
            availableDrivers={availableDrivers}
            conflictResolutions={conflictResolutions}
            unmatchedActions={unmatchedActions}
            onConflictResolutionChange={onConflictResolutionChange}
            onUnmatchedActionChange={onUnmatchedActionChange}
            onMatchRejected={onMatchRejected}
            onMatchRestored={onMatchRestored}
          />
        ))}
      </div>
    </div>
  )
}

interface CompactDriverRowProps {
  row: DriverUpdateRow
  availableDrivers: DriverMaster[]
  conflictResolutions: Map<string, FieldConflictResolution[]>
  unmatchedActions: Map<string, UnmatchedDriverAction>
  onConflictResolutionChange: (rowId: string, conflict: FieldConflictResolution) => void
  onUnmatchedActionChange: (rowId: string, action: UnmatchedDriverAction) => void
  onMatchRejected: (rowId: string) => void
  onMatchRestored: (rowId: string) => void
}

function CompactDriverRow({
  row,
  availableDrivers,
  conflictResolutions,
  unmatchedActions,
  onConflictResolutionChange,
  onUnmatchedActionChange,
  onMatchRejected,
  onMatchRestored
}: CompactDriverRowProps) {
  const [isOpen, setIsOpen] = useState(false)

  const displayIdentifier = row.identifiers.personalCode || row.identifiers.email || row.identifiers.phone || row.identifiers.uuid || ''
  const isRejected = row.hasMatch && unmatchedActions.has(row.rowId)
  const needsAction = !row.hasMatch || (row.conflicts && row.conflicts.length > 0) || isRejected
  const scoreBadge = row.hasMatch ? getMatchScoreBadge(row) : null

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className={`flex items-center justify-between border rounded px-3 py-1.5 hover:bg-muted/50 transition-colors ${needsAction ? 'border-orange-200 bg-orange-50/30' : 'border-border'}`}>
        {/* Left: Name + identifier + matched name */}
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <CollapsibleTrigger className="p-0.5 hover:bg-muted rounded">
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </CollapsibleTrigger>
          <div className="flex-1 truncate text-sm">
            <span className="font-medium">{row.identifiers.name || `Rad ${row.rowIndex + 1}`}</span>
            {displayIdentifier && (
              <span className="text-muted-foreground"> • {displayIdentifier}</span>
            )}
            {row.hasMatch && row.matchedDriverName && (
              <span className="text-muted-foreground"> → {row.matchedDriverName}</span>
            )}
          </div>
        </div>

        {/* Right: badges + action button */}
        <div className="flex items-center gap-1.5 ml-2">
          {scoreBadge && (
            <Badge variant="outline" className={`text-xs py-0 ${scoreBadge.className}`}>
              {scoreBadge.label}
            </Badge>
          )}

          {row.hasMatch ? (
            <>
              {row.isDuplicateMatch && (
                <Badge variant="outline" className="text-xs text-orange-600 border-orange-500 py-0">
                  Dubblett
                </Badge>
              )}
              {isRejected ? (
                <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-500 py-0">
                  Omatchad
                </Badge>
              ) : (
                <>
                  <Badge variant="outline" className="text-xs text-green-600 border-green-500 py-0">
                    Matchad
                  </Badge>
                  {row.conflicts && row.conflicts.length > 0 && (
                    <Badge variant="outline" className="text-xs text-orange-600 border-orange-500 py-0">
                      {row.conflicts.length} konflikt{row.conflicts.length > 1 ? 'er' : ''}
                    </Badge>
                  )}
                </>
              )}
            </>
          ) : (
            <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-500 py-0">
              Omatchad
            </Badge>
          )}
        </div>
      </div>

      <CollapsibleContent>
        <div className="border-x border-b rounded-b px-3 py-2 bg-muted/20 space-y-2">
          {/* Rejected match: show UnmatchedDriverSection + restore button */}
          {isRejected ? (
            <>
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Matchning avvisad. Välj åtgärd nedan.
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => onMatchRestored(row.rowId)}
                >
                  Återställ matchning
                </Button>
              </div>
              <UnmatchedDriverSection
                row={row}
                availableDrivers={availableDrivers}
                currentAction={unmatchedActions.get(row.rowId)}
                onChange={(action) => onUnmatchedActionChange(row.rowId, action)}
              />
            </>
          ) : (
            <>
              {/* Matched driver info + reject button */}
              {row.hasMatch && (
                <div className="flex items-center justify-between">
                  {row.matchedDriverName && (
                    <div className="text-xs text-muted-foreground">
                      Matchad mot: <span className="font-medium text-foreground">{row.matchedDriverName}</span>
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground ml-auto"
                    onClick={() => onMatchRejected(row.rowId)}
                  >
                    Fel matchning?
                  </Button>
                </div>
              )}

              {/* Conflicts */}
              {row.conflicts && row.conflicts.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold">Fältkonflikter</div>
                  {row.conflicts.map((conflict) => {
                    const resolutions = conflictResolutions.get(row.rowId) || []
                    const selectedResolution = resolutions.find(r => r.fieldName === conflict.fieldName)?.resolution || 'use_new'

                    return (
                      <div key={conflict.fieldName} className="rounded border p-2 bg-background text-xs">
                        <div className="font-medium mb-1">{mapFieldLabel(conflict.fieldName)}</div>
                        <div className="grid grid-cols-2 gap-2 mb-2 text-[11px]">
                          <div className="text-muted-foreground">
                            Befintligt: <span className="text-foreground font-medium">{formatValue(conflict.existingValue)}</span>
                          </div>
                          <div className="text-muted-foreground">
                            Nytt: <span className="text-foreground font-medium">{formatValue(conflict.newValue)}</span>
                          </div>
                        </div>
                        <RadioGroup
                          value={selectedResolution}
                          onValueChange={(resolution) => onConflictResolutionChange(row.rowId, {
                            rowId: row.rowId,
                            rowIndex: row.rowIndex,
                            fieldName: conflict.fieldName,
                            resolution: resolution as FieldConflictResolution['resolution']
                          })}
                          className="flex gap-3"
                        >
                          <Label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                            <RadioGroupItem value="use_new" className="h-3 w-3" />
                            Använd ny
                          </Label>
                          <Label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                            <RadioGroupItem value="keep_existing" className="h-3 w-3" />
                            Behåll
                          </Label>
                          <Label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                            <RadioGroupItem value="skip" className="h-3 w-3" />
                            Hoppa över
                          </Label>
                        </RadioGroup>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Unmatched actions */}
              {!row.hasMatch && (
                <UnmatchedDriverSection
                  row={row}
                  availableDrivers={availableDrivers}
                  currentAction={unmatchedActions.get(row.rowId)}
                  onChange={(action) => onUnmatchedActionChange(row.rowId, action)}
                />
              )}
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

interface UnmatchedDriverSectionProps {
  row: DriverUpdateRow
  availableDrivers: DriverMaster[]
  currentAction?: UnmatchedDriverAction
  onChange: (action: UnmatchedDriverAction) => void
}

function UnmatchedDriverSection({
  row,
  availableDrivers,
  currentAction,
  onChange
}: UnmatchedDriverSectionProps) {
  const action = currentAction ?? {
    rowId: row.rowId,
    rowIndex: row.rowIndex,
    action: 'create_new' as UnmatchedDriverAction['action'],
    newDriverData: {
      primaryName: row.identifiers.name || '',
      email: row.identifiers.email,
      phone: row.identifiers.phone,
      personalCode: row.identifiers.personalCode
    }
  }

  const handleActionChange = (newAction: UnmatchedDriverAction['action']) => {
    onChange({
      ...action,
      action: newAction,
      selectedDriverId: newAction === 'select_existing' ? action.selectedDriverId : undefined
    })
  }

  return (
    <div className="rounded border bg-background p-2 space-y-2 text-xs">
      <div>
        <Label className="text-xs font-medium mb-1 block">Välj åtgärd</Label>
        <Select value={action.action} onValueChange={(value) => handleActionChange(value as UnmatchedDriverAction['action'])}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="create_new">Skapa ny förare</SelectItem>
            <SelectItem value="select_existing">Koppla till befintlig förare</SelectItem>
            <SelectItem value="skip">Hoppa över</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {action.action === 'select_existing' && (
        <div>
          <Label className="text-xs font-medium mb-1 block">Välj förare</Label>
          <Select
            value={action.selectedDriverId ?? ''}
            onValueChange={(value) => onChange({
              ...action,
              action: 'select_existing',
              selectedDriverId: value
            })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Välj befintlig förare" />
            </SelectTrigger>
            <SelectContent>
              {availableDrivers.map((driver) => (
                <SelectItem key={driver.id} value={driver.id}>
                  {driver.primaryName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {action.action === 'create_new' && (
        <div className="space-y-2">
          <div className="grid gap-2 grid-cols-2">
            <div>
              <Label className="text-xs font-medium mb-1 block">Namn</Label>
              <Input
                className="h-8 text-xs"
                value={action.newDriverData?.primaryName ?? ''}
                onChange={(event) => onChange({
                  ...action,
                  action: 'create_new',
                  newDriverData: {
                    ...action.newDriverData,
                    primaryName: event.target.value
                  }
                })}
                placeholder="Förarens namn"
              />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">E-post</Label>
              <Input
                className="h-8 text-xs"
                value={action.newDriverData?.email ?? ''}
                onChange={(event) => onChange({
                  ...action,
                  action: 'create_new',
                  newDriverData: {
                    ...action.newDriverData,
                    email: event.target.value
                  }
                })}
                placeholder="namn@example.com"
              />
            </div>
          </div>
          <div className="grid gap-2 grid-cols-2">
            <div>
              <Label className="text-xs font-medium mb-1 block">Telefon</Label>
              <Input
                className="h-8 text-xs"
                value={action.newDriverData?.phone ?? ''}
                onChange={(event) => onChange({
                  ...action,
                  action: 'create_new',
                  newDriverData: {
                    ...action.newDriverData,
                    phone: event.target.value
                  }
                })}
                placeholder="+46..."
              />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">Personnummer</Label>
              <Input
                className="h-8 text-xs"
                value={action.newDriverData?.personalCode ?? ''}
                onChange={(event) => onChange({
                  ...action,
                  action: 'create_new',
                  newDriverData: {
                    ...action.newDriverData,
                    personalCode: event.target.value
                  }
                })}
                placeholder="ÅÅÅÅMMDD-XXXX"
              />
            </div>
          </div>
        </div>
      )}

      {action.action === 'skip' && (
        <div className="text-[11px] text-muted-foreground">
          Raden kommer att hoppas över och inga ändringar görs.
        </div>
      )}
    </div>
  )
}

function getMatchScoreBadge(row: DriverUpdateRow): { label: string; className: string } | null {
  const score = row.matchScore
  if (score === undefined) return null

  const isFuzzy = row.matchType === 'fuzzy' || row.matchType === 'alias'
  const suffix = isFuzzy ? ' ≈' : ''
  const label = `${score}%${suffix}`

  if (score === 100) return { label, className: 'text-green-700 border-green-600' }
  if (score >= 90) return { label, className: 'text-blue-800 border-blue-700' }
  if (score >= 80) return { label, className: 'text-blue-600 border-blue-500' }
  if (score >= 70) return { label, className: 'text-yellow-600 border-yellow-500' }
  return null
}

function mapFieldLabel(field: string): string {
  switch (field) {
    case 'primaryName':
    case 'driverName':
      return 'Namn'
    case 'email':
      return 'E-post'
    case 'phone':
      return 'Telefon'
    case 'personalCode':
      return 'Personnummer'
    case 'taxiCode':
      return 'Taxikod'
    case 'employeeNumber':
      return 'Anställningsnummer'
    case 'bankAccount':
      return 'Bankkonto'
    case 'paymentReference':
      return 'Betalningsreferens'
    case 'taxTableName':
      return 'Skattetabell'
    case 'taxColumn':
      return 'Skattekolumn'
    case 'salaryGarnishment':
      return 'Löneutmätning'
    default:
      return field
  }
}

function formatValue(value: unknown): string {
  if (value === undefined || value === null || value === '') {
    return 'saknas'
  }
  if (typeof value === 'number') {
    return value.toLocaleString('sv-SE')
  }
  return String(value)
}
